// Combat: siege transitions, target acquisition, weapons, damage and splash.
//
// OWNS (fields on entities)
//   weaponCd, target, sieged, siegeTimer, siegePending
//   postX / postY        leash anchor -- the spot a unit walks back to after a chase
//   leashing             true while it is walking back to that spot
//   noAcqId / noAcqUntil one-slot "stop chasing that guy" memory, set on a leash break
//   lastDamagedTick / lastAttackerId   (read by fog.js for the under-attack alerts)
//   _cb                  private "combat fields installed" flag, see initCombat()
//   e.facing while firing, e.moveTarget while closing on a target
//
// EVENTS EMITTED -- the shapes render/effects.js and the HUD read. Every one of
// them also carries `tick`, stamped by world.emit().
//   attack  { from, to, weapon, x, y, tx, ty, splash, owner }
//           `weapon` is the PROJECTILE name ('bullet'|'laser'|'shell'|'missile'|
//           'arc') or null for melee; x,y is the muzzle and tx,ty the impact.
//           Exactly one `splash` event follows each attack with splash > 0, in
//           the same tick and after it: effects.js pairs them to spawn the blast
//           ring when the shell lands rather than when it is fired.
//   damage  { id, amount, x, y, owner }   owner is the VICTIM's owner
//   splash  { x, y, radius }
//   death   { id, owner, kind, defId, x, y }   emitted by world.reap(), not here
//
// ORDER HANDLING -- why this system writes e.order
//   Attack-move and patrol are advances that interrupt THEMSELVES to fight. When
//   such a unit acquires a target its order is swapped for an internal
//     { type:'attack', auto:true, resume:<the original order object> }
//   and swapped back the instant the fight ends, so the advance survives the kill.
//   That round trip is what makes a-move feel like a-move. Without it the unit
//   either keeps walking past the enemy while shooting (movement.js drives plain
//   'attackmove' straight at the ordered point) or -- worse -- movement's arrival
//   check fires on the combat-issued approach and calls nextOrder(), which throws
//   the destination away after the first kill. The swapped-in order is not in
//   movement's targetOf() switch, so movement only ever moves the unit where
//   combat asks it to, and orderQueue is left untouched throughout.
//
// NO ROUTE -- combat reads movement's reachability verdict
//   movement.js counts consecutive ticks where the flow field proves there is no
//   route from a unit to where it is being sent, in e._unreach. The field is a
//   complete reachability answer for the whole map, so that is a verdict and not
//   a guess, and both of the ways a fight can be started at something you cannot
//   walk to need it:
//
//   * An ADVANCE whose goal is walled off. movement's own answer is to drop the
//     order (it is impossible), which quietly deletes half a squad's a-move at
//     the start line while the other half -- the ones that happened to be inside
//     acquisition range of the wall -- fight. BW sends the whole squad at the
//     buildings in the way. So before movement gives up, combat looks for the
//     enemy structure blocking the path and engages it as a BREAKTHROUGH: the
//     same order swap as any other engagement, flagged so the wall is kept as a
//     target at any range and never leashed (a building cannot drag anyone
//     anywhere). Kill it, and `resume` hands the advance straight back.
//   * A CHASE that can never close: a target visible across a wall or a cliff.
//     Combat used to rewrite e.moveTarget every tick and ignore the verdict, so
//     the unit ground against the obstacle in moveState 'moving' forever. BW
//     walks a unit as close as it can get and the order fails there; so does
//     this, and the runner is blacklisted the same way a broken leash blacklists
//     one, so the next pass does not immediately re-lock it.
//
//   Both only trust the counter while the unit is actually walking (moveState
//   'moving'), because movement does not refresh it for a unit that has stopped.
//
// Damage follows BW's type-vs-size table (explosive is weak to small units,
// concussive is weak to large ones) which is what gives the five unit types
// real rock-paper-scissors rather than a stat ladder.

import { UnitDefs, BuildingDefs } from './defs.js';
import { TICK_DT, DAMAGE_TABLE, MIN_DAMAGE, ACQUIRE_RANGE_BONUS, LEASH_RANGE } from './constants.js';
import { nextOrder } from './commands.js';
import { canSee } from './fog.js';
import { edgeDist } from './world.js';

// --- tuning that belongs to this system ------------------------------------
// (constants.js is shared and not mine to edit; anything here that other systems
// need to agree on is reported as a change request instead.)

/** Ticks between "is there a better target?" passes. 8 == 3 Hz. */
const RETARGET_PERIOD = 8;
/** A rival target must beat the current one by this much score to steal focus. */
const RETARGET_MARGIN = 2.5;
/** Score bonus for a target that can shoot back, in tiles-of-distance. */
const THREAT_BONUS = 3.0;
/** Score bonus for a target at 0% hp, scaled by how wounded it is. */
const WOUND_BONUS = 1.5;
/** Structures are worth less than the army standing next to them. */
const BUILDING_PENALTY = 3.0;
/** Slack past acquisition range before a locked target is considered gone. */
const KEEP_SLACK = 2.0;
/**
 * Floor on auto-acquisition range. A bruiser's weapon reaches 1.0 tiles; without
 * a floor it would stand and watch a rifleman plink it from 4.5 like a lemon,
 * which is not what a zealot does when something walks past it. BW gives every
 * ground unit a seek range well past its weapon; this is that. Keep it under
 * LEASH_RANGE so a unit that charges from the very edge of acquisition can still
 * reach its target without immediately snapping back.
 */
const MIN_ACQUIRE = 5.5;
/** After a leash break, ignore that specific runner for this many ticks. */
const LEASH_IGNORE_TICKS = 72;
/**
 * Consecutive "no route" ticks before an advance goes through the wall instead.
 *
 * movement.js drops an impossible move/attack-move order after UNREACH_TICKS
 * (6) and combat runs FIRST in the tick, so this has to fire while the order is
 * still there to be rescued -- it is deliberately well under that budget. Four
 * ticks is a sixth of a second, and a unit that a building was just stamped on
 * top of is walked back out by movement with the counter reset, so this cannot
 * fire on the transient.
 */
const UNREACH_ENGAGE = 3;
/** Consecutive "no route" ticks before a chase gives up on its target. */
const UNREACH_DROP = 12;
/**
 * How far around itself a blocked unit looks for the structure in its way.
 *
 * Comfortably past every unit's sight radius, because the real bound on this is
 * vision, not distance: findBlocker() will not target a structure the owner
 * cannot currently see, so in practice the search reaches as far as the squad
 * (or whatever is spotting for it) can actually see.
 */
const BLOCK_SCAN = 14;
/** Walk back to the post if the fight ended more than this far from it. */
const RETURN_EPS = 2.0;
/** Padding on the spatial query so big footprints whose CENTRE is out of range
 *  but whose EDGE is in range are still found. */
const QUERY_PAD = 3.5;
/**
 * "Never happened" stamp for lastDamagedTick. fog.js tests it as
 * `lastDamagedTick >= tick - FOG_INTERVAL`, so a sentinel near zero reads as
 * fresh damage during the opening ticks and pings the minimap for every unit on
 * the map at 0:00. Far enough back that no comparison can ever mistake it, and
 * still a small integer.
 */
const NEVER = -1000000;

const _near = [];
const _blast = [];
const _hits = [];
/** Ids of everything driven to 0 hp during THIS tick's combat pass. */
const _slain = [];

export function weaponOf(e) {
  if (e.kind === 'building') return BuildingDefs[e.defId].weapon || null;
  const d = UnitDefs[e.defId];
  if (!d) return null;
  return e.sieged && d.siegeWeapon ? d.siegeWeapon : d.weapon || null;
}

export function canTarget(w, attacker, target) {
  if (!w || !target || !target.alive) return false;
  if (target.owner === attacker.owner) return false;
  if (target.owner < 0 || target.kind === 'resource') return false;
  if (target.hp <= 0) return false;
  if (target.flying) return w.targets === 'air' || w.targets === 'any';
  return w.targets === 'ground' || w.targets === 'any';
}

export function damageAmount(w, target) {
  const mult = (DAMAGE_TABLE[w.type] || DAMAGE_TABLE.normal)[target.kind === 'building' ? 2 : target.size ?? 1];
  return Math.max(MIN_DAMAGE, Math.round(w.damage * mult) - (target.armor || 0));
}

/**
 * Weapon range is measured edge-to-edge, never centre-to-centre.
 *
 * A 4x4 structure is 2.0 tiles from its centre through a face and 2.83 through a
 * corner; a single radial threshold against the centre therefore either refuses
 * to shoot a building you are standing on or lets you shoot one from thin air.
 * Same reason economy.js measures mining with edgeDist().
 */
export function gapTo(e, t) {
  if (t.tx !== undefined) return Math.max(0, edgeDist(t, e.x, e.y) - (e.radius || 0));
  return Math.max(0, Math.hypot(t.x - e.x, t.y - e.y) - (t.radius || 0) - (e.radius || 0));
}

/** Radial stop distance (from the target's CENTRE) that puts `e` inside `w.range`. */
function chaseTo(e, w, t) {
  const half = t.tx !== undefined ? Math.max(t.w, t.h) / 2 : (t.radius || 0);
  return { x: t.x, y: t.y, stopDist: Math.max(0.4, half + (e.radius || 0) + w.range * 0.9) };
}

/**
 * Install every combat-owned optional field in ONE fixed order the first time
 * this system sees an entity.
 *
 * world.js seeds the fields it documents (weaponCd, target, sieged, siegeTimer,
 * cloaked, detected); the post/leash bookkeeping below is ours. Adding it lazily
 * -- only to the units that actually end up in a fight, and in whichever order
 * that particular fight happened to need -- gives fighters, retaliators and
 * pacifists three different hidden classes for what is supposed to be one
 * monomorphic entity shape, and every property read in this file pays for it.
 * movement.js installs its `_mv*` scratch the same way and for the same reason.
 *
 * Values already present are preserved: commands.js can set siegePending in the
 * same tick a tank is trained, before combat has ever looked at it.
 */
function initCombat(e) {
  const sp = e.siegePending;
  e._cb = 1;
  e.postX = undefined; e.postY = undefined;
  e.leashing = false;
  e.noAcqId = 0; e.noAcqUntil = NEVER;
  e.siegePending = sp === undefined ? false : sp;
  e.lastDamagedTick = NEVER; e.lastAttackerId = null;
}

export function combatSystem(world) {
  _slain.length = 0;
  for (let p = 0; p < 2; p++) {
    for (const e of world.unitsOf(p)) { if (e._cb !== 1) initCombat(e); tickUnit(world, e); }
    for (const b of world.buildingsOf(p)) { if (b._cb !== 1) initCombat(b); tickTurret(world, b); }
  }
  if (_slain.length) forgetTheDead(world);
}

/**
 * Drop every reference to something that died during this pass.
 *
 * world.reap() does not run until the end of the tick, so without this an
 * entity that fires the killing shot -- or that was simply pointed at the same
 * victim as the unit which did -- carries a `target` id across the tick
 * boundary that no longer resolves to anything. combat.js owns `target`, and
 * the renderer and HUD read it between logic frames, so the invariant that it
 * always names a live entity has to hold when the tick ends, not merely be
 * repaired on the next one. The matching `moveTarget` goes too: a unit should
 * never take even one step towards a corpse.
 *
 * O(entities) and only on ticks where something actually died.
 */
function forgetTheDead(world) {
  for (let p = 0; p < 2; p++) {
    for (const id of world.byPlayer[p]) {
      const e = world.entities.get(id);
      if (!e || e.target == null) continue;
      if (_slain.indexOf(e.target) === -1) continue;
      e.target = null;
      if (e.kind === 'unit') e.moveTarget = null;
    }
  }
}

// ---------------------------------------------------------------------------
// Units
// ---------------------------------------------------------------------------
function tickUnit(world, e) {
  if (e.weaponCd > 0) e.weaponCd -= TICK_DT;

  // Siege / unsiege transition: 2 s of being a sitting duck, no weapon at all.
  // Nothing in this file ever starts a transition -- (un)sieging is a deliberate
  // player command, so a sieged tank never wanders off to chase something.
  if (e.siegeTimer > 0) {
    e.siegeTimer -= TICK_DT;
    e.vx = 0; e.vy = 0;
    e.moveTarget = null;
    if (e.siegeTimer <= 0) {
      e.siegeTimer = 0;
      e.sieged = !!e.siegePending;
      e.siegePending = false;
      e.target = null;
      world.emit({ type: e.sieged ? 'sieged' : 'unsieged', id: e.id, x: e.x, y: e.y });
    }
    return;
  }

  const w = weaponOf(e);
  if (!w) { e.target = null; return; }

  const def = UnitDefs[e.defId];
  let o = e.order;
  const ot = o ? o.type : null;

  // A move order is a move order, and a worker on the job stays on the job:
  // BW never pulls a mining SCV into a fight, and neither do we.
  if (ot === 'move' || ot === 'gather' || ot === 'build' || ot === 'repair') {
    e.target = null;
    clearPost(e);
    return;
  }

  const explicit = ot === 'attack' && !o.auto;
  const auto     = ot === 'attack' && !!o.auto;
  const advance  = ot === 'attackmove' || ot === 'patrol';
  const holding  = ot === 'hold';
  const idle     = !o;
  const stationary = holding || e.sieged;

  let tgt = null;
  // True only while this unit is walking a wall down on the advance's behalf.
  let breaking = false;

  if (explicit) {
    // Player-issued attack: obey it exactly -- no retargeting, no leash.
    clearPost(e);
    const ordered = world.alive(o.targetId);
    if (!ordered || !canTarget(w, e, ordered)) { e.target = null; nextOrder(e); return; }
    if (!canSee(world, e.owner, ordered)) {
      // Lost it in the fog (or it cloaked): walk to where it was last seen and
      // give up there. Tracking a live position through fog would make an attack
      // order a free wallhack.
      e.target = null;
      if (o.lastX === undefined || stationary) { nextOrder(e); return; }
      if (Math.hypot(e.x - o.lastX, e.y - o.lastY) < 1.2) { nextOrder(e); return; }
      e.moveTarget = { x: o.lastX, y: o.lastY, stopDist: 0.6 };
      return;
    }
    o.lastX = ordered.x; o.lastY = ordered.y;
    tgt = ordered;
  } else {
    tgt = e.target != null ? world.alive(e.target) : null;
    // A breakthrough only stays one while the wall it was opened against is
    // still the focus; the moment a live enemy steals it this is an ordinary
    // engagement again, leash and all.
    breaking = auto && !!o.breakthrough && !!tgt && tgt.id === o.targetId;
    if (tgt && !(breaking ? stillTargetable(world, e, w, tgt) : stillValid(world, e, w, tgt, stationary))) {
      tgt = null;
      breaking = false;
    }

    // Workers never auto-acquire while idle (they retaliate, see applyDamage);
    // a worker the player attack-moved is a soldier and does acquire.
    const mayAcquire = !(idle && def.isWorker);
    if (mayAcquire && (!tgt || retargetDue(world, e))) {
      const range = acquireRange(e, w, stationary);
      const found = acquire(world, e, w, range, tgt);
      if (found !== tgt) { tgt = found; breaking = false; }
    }

    // Nothing to shoot and no way through: the advance is walled off, so make
    // the wall the fight. Without this movement drops the impossible order a
    // couple of ticks from now and the unit stands at the start line for the
    // rest of the match while its squadmates take the wall down without it.
    if (!tgt && advance && noRoute(e, UNREACH_ENGAGE)) {
      // Patrol walks its second leg backwards, so ask the order where this unit
      // is actually headed rather than assuming it is the ordered point.
      const back = ot === 'patrol' && o.leg === 1;
      const blocker = findBlocker(world, e, w, back ? o.fromX : o.x, back ? o.fromY : o.y);
      if (blocker) { tgt = blocker; breaking = true; }
    }

    if (!tgt && auto) {
      // the thing we were sent to kill is gone: hand the advance back
      endEngagement(world, e);
      return;
    }
    if (tgt && advance) { beginEngagement(world, e, tgt, o, breaking); o = e.order; }
    else if (tgt && e.postX === undefined) { e.postX = e.x; e.postY = e.y; }

    // The post travels with a unit that is walking a wall down, so the leash
    // measures from the front line rather than from wherever the advance
    // happened to stall -- and the anchor is already in the right place if a
    // defender pops out and steals focus.
    if (breaking) { e.postX = e.x; e.postY = e.y; e.leashing = false; }
  }

  if (!tgt) {
    e.target = null;
    if (!stationary && e.postX !== undefined) {
      const back = Math.hypot(e.x - e.postX, e.y - e.postY);
      if (back > RETURN_EPS) { e.leashing = true; e.moveTarget = { x: e.postX, y: e.postY, stopDist: 0.5 }; return; }
    }
    clearPost(e);
    return;
  }

  e.target = tgt.id;

  const gap = gapTo(e, tgt);
  const tooClose = w.minRange != null && gap < w.minRange;

  if (gap <= w.range && !tooClose) {
    e.facing = Math.atan2(tgt.y - e.y, tgt.x - e.x);
    if (e.weaponCd <= 0) fire(world, e, w, tgt);
    e.moveTarget = null;             // stand and shoot; never drift onto the target
    e.leashing = false;
    return;
  }

  if (stationary) {
    // Hold-position and sieged units fire but never take a step. If the target
    // walked out of range -- or inside the siege minimum range -- drop it so the
    // next pass can lock something we can actually hit.
    e.target = null;
    return;
  }

  // No route to it. See "NO ROUTE" at the top: walk as close as we can get and
  // let the order fail there instead of re-verifying the same impossible path
  // every tick forever. A breakthrough is exempt -- its target is the wall
  // itself, whose own footprint is by definition somewhere nobody can stand.
  if (!breaking && noRoute(e, UNREACH_DROP)) {
    e.noAcqId = tgt.id;
    e.noAcqUntil = world.tick + LEASH_IGNORE_TICKS;
    e.target = null;
    if (explicit) { nextOrder(e); return; }        // the player's order is over
    if (auto) { endEngagement(world, e); return; } // hand the advance back
    e.moveTarget = null;
    return;
  }

  // Chase, but on a leash: one runner must not be able to drag an army across
  // the map. Breaking the leash also blacklists that runner for a few seconds so
  // the unit does not immediately re-acquire it and yo-yo.
  if (e.postX !== undefined && Math.hypot(e.x - e.postX, e.y - e.postY) > LEASH_RANGE) {
    e.noAcqId = tgt.id;
    e.noAcqUntil = world.tick + LEASH_IGNORE_TICKS;
    e.target = null;
    if (auto) { endEngagement(world, e); return; }      // resume the advance from here
    e.leashing = true;
    e.moveTarget = { x: e.postX, y: e.postY, stopDist: 0.5 };
    return;
  }

  e.moveTarget = chaseTo(e, w, tgt);
}

/**
 * Swap an advance order for an engagement that remembers it.
 *
 * `breakthrough` marks the engagement as "this structure is in the way of the
 * advance", which changes two things: the target is kept however far away it
 * is (the whole point is to walk the length of the approach and knock it down)
 * and the leash never fires against it. Every field is written on every path so
 * the internal order object keeps one shape.
 */
function beginEngagement(world, e, tgt, order, breakthrough = false) {
  e.order = {
    type: 'attack', targetId: tgt.id, auto: true, resume: order,
    x: order.x, y: order.y, breakthrough,
  };
  e.postX = e.x; e.postY = e.y;
  e.leashing = false;
}

/** Hand an auto-engaged unit back the advance it interrupted. */
function endEngagement(world, e) {
  const o = e.order;
  e.target = null;
  if (o && o.type === 'attack' && o.auto && o.resume) {
    e.order = o.resume;              // same object: patrol keeps its leg, a-move its point
    clearPost(e);
    e.moveTarget = null;             // movement drives the advance from the order again
  } else {
    nextOrder(e);
  }
}

function clearPost(e) {
  if (e.postX !== undefined) { e.postX = undefined; e.postY = undefined; }
  e.leashing = false;
}

function acquireRange(e, w, stationary) {
  // Something that cannot move must not lock a target it cannot reach, and a
  // unit walking home on a broken leash only stops for things already in range.
  if (stationary || e.leashing) return w.range;
  return Math.max(w.range + ACQUIRE_RANGE_BONUS, MIN_ACQUIRE);
}

const retargetDue = (world, e) => ((world.tick + e.id) % RETARGET_PERIOD) === 0;

/** Can this target be shot at all right now, range aside? */
function stillTargetable(world, e, w, t) {
  if (!canTarget(w, e, t)) return false;
  if (!canSee(world, e.owner, t)) return false;
  if (w.minRange != null && gapTo(e, t) < w.minRange) return false;   // inside the dead zone
  return true;
}

/** Is the target we already have still worth keeping? */
function stillValid(world, e, w, t, stationary) {
  if (!stillTargetable(world, e, w, t)) return false;
  return gapTo(e, t) <= acquireRange(e, w, stationary) + (stationary ? 0 : KEEP_SLACK);
}

/**
 * movement.js's reachability verdict, read but never written here.
 *
 * `_unreach` counts consecutive ticks in which the flow field -- a complete
 * reachability answer for the whole map, not a heuristic -- had no route from
 * this unit to wherever it is being sent. movement does not refresh the counter
 * for a unit that has stopped, so the moveState gate is what keeps a stale
 * count from condemning the next chase this unit starts.
 */
const noRoute = (e, ticks) => e.moveState === 'moving' && e._unreach > ticks;

/**
 * The enemy structure standing between this unit and where it was sent.
 *
 * Nearest wins, but only among buildings that actually shorten the trip -- a
 * depot BEHIND the squad is not what is blocking it -- and only ones the owner
 * can see, so a walled-off a-move never turns into a wallhack that reveals
 * where the enemy built. Nothing found (an island, a cliff, an unscouted wall)
 * leaves movement's give-up to run exactly as before.
 */
function findBlocker(world, e, w, gx, gy) {
  const toGoal = Math.hypot(gx - e.x, gy - e.y);
  world.queryRadius(e.x, e.y, BLOCK_SCAN + QUERY_PAD, _near);

  let best = null, bestGap = Infinity;
  for (let i = 0; i < _near.length; i++) {
    const o = _near[i];
    if (o.kind !== 'building' || !o.alive) continue;
    if (!canTarget(w, e, o)) continue;
    const gap = gapTo(e, o);
    if (gap > BLOCK_SCAN || gap >= bestGap) continue;
    if (Math.hypot(gx - o.x, gy - o.y) >= toGoal) continue;      // not on the way
    if (!canSee(world, e.owner, o)) continue;
    best = o; bestGap = gap;
  }
  return best;
}

// ---------------------------------------------------------------------------
// Buildings (missile turrets)
// ---------------------------------------------------------------------------
function tickTurret(world, b) {
  if (b.constructing) return;
  const w = BuildingDefs[b.defId].weapon;
  if (!w) return;
  if (b.weaponCd > 0) b.weaponCd -= TICK_DT;

  let tgt = b.target != null ? world.alive(b.target) : null;
  if (tgt && !stillValid(world, b, w, tgt, true)) tgt = null;
  if (!tgt || retargetDue(world, b)) tgt = acquire(world, b, w, w.range, tgt);

  b.target = tgt ? tgt.id : null;
  if (tgt && b.weaponCd <= 0) fire(world, b, w, tgt);
}

// ---------------------------------------------------------------------------
// Acquisition
// ---------------------------------------------------------------------------
/**
 * Best legal, visible enemy within `range` (edge-to-edge).
 *
 * Priority: things that shoot back first, then closer, then already-wounded,
 * with structures deprioritised behind the army defending them. `current` is the
 * target we already have -- a rival has to beat it by RETARGET_MARGIN to steal
 * focus, which is what stops a unit standing between two equidistant enemies
 * from re-acquiring every single frame and never finishing its cooldown.
 */
function acquire(world, e, w, range, current) {
  const ignore = e.noAcqUntil > world.tick ? e.noAcqId : null;
  world.queryRadius(e.x, e.y, range + QUERY_PAD, _near);

  let best = null, bestScore = Infinity;
  for (let i = 0; i < _near.length; i++) {
    const o = _near[i];
    if (o.owner === e.owner || o.owner < 0 || !o.alive) continue;
    if (o.kind === 'resource') continue;
    if (o.id === ignore) continue;
    if (!canTarget(w, e, o)) continue;
    const gap = gapTo(e, o);
    if (gap > range) continue;
    if (w.minRange != null && gap < w.minRange) continue;   // unhittable, do not lock it
    if (!canSee(world, e.owner, o)) continue;
    const s = score(e, w, o, gap);
    if (s < bestScore) { bestScore = s; best = o; }
  }

  if (!best) return current || null;                       // nothing better than what we have
  if (current && current.alive) {
    const cs = score(e, w, current, gapTo(e, current));
    if (bestScore > cs - RETARGET_MARGIN) return current;
  }
  return best;
}

function score(e, w, t, gap) {
  let s = gap;
  if (isThreat(t)) s -= THREAT_BONUS;
  if (t.kind === 'building') s += BUILDING_PENALTY;
  s -= (1 - t.hp / t.maxHp) * WOUND_BONUS;
  return s;
}

/** "Shoots back" for priority purposes: a worker's scratch attack does not count,
 *  and a half-built turret is not shooting anyone yet. */
function isThreat(t) {
  if (!weaponOf(t)) return false;
  if (t.kind === 'building') return !t.constructing;
  return !UnitDefs[t.defId].isWorker;
}

// ---------------------------------------------------------------------------
// Firing and damage
// ---------------------------------------------------------------------------
function fire(world, attacker, w, target) {
  attacker.weaponCd = w.cooldown;
  world.emit({
    type: 'attack', from: attacker.id, to: target.id, weapon: w.projectile,
    x: attacker.x, y: attacker.y, tx: target.x, ty: target.y,
    splash: w.splash || 0, owner: attacker.owner,
  });
  if (w.splash > 0) applySplash(world, attacker, w, target.x, target.y);
  else applyDamage(world, attacker, target, damageAmount(w, target));
}

/**
 * BW-style three-ring falloff. Friendlies are NOT spared -- an unattended siege
 * line deleting its own infantry is the whole reason siege tanks need managing.
 * The firing unit itself is exempt (a tank cannot shell its own hull; the 2.0
 * minimum range keeps the blast at arm's length anyway).
 */
function applySplash(world, attacker, w, x, y) {
  world.queryRadius(x, y, w.splash + QUERY_PAD, _blast);
  // Copy out of the shared query buffer before dealing any damage -- applyDamage
  // runs retaliation logic and a future caller could re-enter queryRadius, which
  // would rewrite `_blast` underneath the loop. A persistent buffer buys that
  // safety without allocating an array on every single shell.
  const n = _blast.length;
  for (let i = 0; i < n; i++) _hits[i] = _blast[i];
  for (let i = 0; i < n; i++) {
    const o = _hits[i];
    _hits[i] = null;                 // never retain a reaped entity in scratch
    if (!o.alive || o.hp <= 0 || o.kind === 'resource' || o.owner < 0) continue;
    if (o === attacker) continue;
    if (o.flying && w.targets === 'ground') continue;
    if (!o.flying && w.targets === 'air') continue;
    const d = o.tx !== undefined ? edgeDist(o, x, y) : Math.max(0, Math.hypot(o.x - x, o.y - y) - (o.radius || 0));
    if (d > w.splash) continue;
    const f = d <= w.splash * 0.34 ? 1 : d <= w.splash * 0.67 ? 0.5 : 0.25;
    applyDamage(world, attacker, o, Math.max(MIN_DAMAGE, Math.round(damageAmount(w, o) * f)));
  }
  world.emit({ type: 'splash', x, y, radius: w.splash });
}

export function applyDamage(world, attacker, target, amount) {
  if (!target.alive || target.hp <= 0) return;
  // Init before the first write, never after: initCombat() resets
  // lastDamagedTick, and a structure shot in the same tick it first appears
  // would otherwise have its "under attack" stamp wiped by its own init pass.
  if (target._cb !== 1) initCombat(target);
  target.hp -= amount;
  target.lastDamagedTick = world.tick;
  target.lastAttackerId = attacker ? attacker.id : null;
  world.emit({ type: 'damage', id: target.id, amount, x: target.x, y: target.y, owner: target.owner });
  if (target.hp <= 0) {
    // reap() will not delete it until the end of the tick, so note it here and
    // let forgetTheDead() strip the stale `target` ids before anyone reads them.
    if (_slain.indexOf(target.id) === -1) _slain.push(target.id);
    if (attacker && attacker.owner >= 0 && attacker.owner !== target.owner) world.players[attacker.owner].stats.unitsKilled++;
    return;
  }

  // Retaliation. BW does not pull a mining worker off the patch when it is shot,
  // and neither do we -- a gather order is an order, so the whole mineral line
  // keeps working while one worker takes hits. Only a genuinely idle unit (or one
  // holding position, which fires without moving) turns and fights back, and only
  // if it has nothing better in its sights already.
  if (target.kind !== 'unit' || !attacker || target.target != null) return;
  const o = target.order;
  if (o && o.type !== 'hold') return;
  const tw = weaponOf(target);
  if (!tw || !canTarget(tw, target, attacker)) return;
  if (!canSee(world, target.owner, attacker)) return;
  target.target = attacker.id;
  if (target.postX === undefined) { target.postX = target.x; target.postY = target.y; }
  target.leashing = false;
}
